import React, { useEffect, useState } from "react";
import {
  Typography,
  IconButton,
  Box,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { SiTether } from "react-icons/si";
import Preloader from "./components/Preloader";
import { useParams, useNavigate } from "react-router-dom";
import { getExchangeRate } from "./api/exchangeRate";
import { useWallet } from "./api/hooks/useWallet";
import { useExchangeRate } from "./api/hooks/useExchangeRate";

export default function WalletConvert({ setSidebarOpen }) {
  const { sourceWalletId } = useParams();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [error, setError] = useState("");

  // Convert state
  const [activeStep, setActiveStep] = useState(0);
  const [sourceWallet, setSourceWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [toCurrency, setToCurrency] = useState("NGN");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");
  const [exchangeRateInfo, setExchangeRateInfo] = useState(null);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [convertResult, setConvertResult] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const { data: walletsData, isLoading } = useWallet();
  const {
    mutate,
    data: exchangeData,
    isPending,
    isError,
    error: exchangeError,
  } = useExchangeRate();

  const steps = [
    "Enter Amount",
    "Get Exchange Rate",
    "Confirm Conversion",
    "Complete",
  ];
  const supportedCurrencies = ["NGN", "USD", "GBP"];

  useEffect(() => {
    if (walletsData) {
      setWallets(walletsData.data ?? []);

      if (sourceWalletId) {
        const foundWallet = walletsData.data.find(
          (w) => w.id === sourceWalletId || w.wallet_number === sourceWalletId
        );
        if (foundWallet) {
          setSourceWallet(foundWallet);
        }
      }
    }
  }, [walletsData, sourceWalletId]);

  // Helper functions
  function getFlag(code) {
    if (code === "NGN") return "🇳🇬";
    if (code === "USD") return "🇺🇸";
    if (code === "USDT") return "USDT";
    if (code === "GBP") return "🇬🇧";
    return "💰";
  }

  function getSymbol(code) {
    if (code === "NGN") return "₦";
    if (code === "USD" || code === "USDT") return "$";
    if (code === "GBP") return "£";
    return "";
  }

  function getCurrencyCode(wallet) {
    if (!wallet) return "N/A";

    if (wallet.provider_metadata) {
      try {
        const meta = JSON.parse(wallet.provider_metadata);
        if (meta.currency) return meta.currency;
      } catch { }
    }
    return wallet.currency?.code || wallet.currency?.name || "N/A";
  }

  const handleNext = () => {
    if (activeStep === 0) {
      if (!amount || Number(amount) <= 0) {
        setConvertError("Enter a valid amount");
        return;
      }
      setActiveStep(1);
      setConvertError("");
    } else if (activeStep === 1) {
      setActiveStep(2);
      setConvertError("");
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setConvertError("");
  };

  const handleGetExchangeRate = async () => {
    setExchangeRateLoading(true);
    setConvertError("");

    try {
      const payload = {
        base_currency: sourceWallet ? getCurrencyCode(sourceWallet) : "NGN",
        quote_currency: toCurrency,
        exchange_amount: Number(amount),
      };

      // @ts-ignore
      mutate(payload); // call your mutation
    } catch (e) {
      setConvertError(
        e?.response?.data?.message ||
        "Failed to get exchange rate. Please try again."
      );
    }
    setExchangeRateLoading(false);
  };

  useEffect(() => {
    if (!exchangeData) return;

    if (exchangeData.status && exchangeData.data) {
      setExchangeRateInfo(exchangeData.data);
      setActiveStep(2);
    } else {
      setConvertError(exchangeData.message || "Failed to get exchange rate.");
    }
  }, [exchangeData]);

  const handleConvert = async () => {
    if (!pin || pin.length !== 4) {
      setConvertError("Please enter a valid 4-digit transaction PIN");
      return;
    }

    setConverting(true);
    setConvertError("");

    try {
      // Here you would implement the actual currency conversion API call
      // For now, we'll simulate a successful conversion
      const conversionResult = {
        status: true,
        message: "Currency conversion completed successfully!",
        data: {
          from_currency: sourceWallet ? getCurrencyCode(sourceWallet) : "NGN",
          from_amount: amount,
          to_currency: toCurrency,
          to_amount: exchangeRateInfo.final_amount,
          exchange_rate: exchangeRateInfo.rate,
          transaction_id: `CONV_${Date.now()}`,
        },
      };

      if (conversionResult.status) {
        setConvertResult(conversionResult.data);
        setActiveStep(3);
        setSnackbarMsg("Currency conversion completed successfully!");
        setSnackbarOpen(true);
      } else {
        setConvertError(conversionResult.message || "Conversion failed.");
      }
    } catch (e) {
      setConvertError(
        e?.response?.data?.message || "Conversion failed. Please try again."
      );
    }
    setConverting(false);
  };

  const handleReset = () => {
    setActiveStep(0);
    setAmount("");
    setToCurrency("NGN");
    setPin("");
    setConvertError("");
    setExchangeRateInfo(null);
    setConvertResult(null);
  };

  const handleGoBack = () => {
    navigate("/wallets");
  };

  if (isLoading)
    return <Preloader fullScreen={true} message="Loading wallets..." />;
  if (error)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-3 sm:p-4">
        <div className="fintech-card p-6 sm:p-8 text-center max-w-sm sm:max-w-md">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-danger-100 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <ArrowBackIcon className="text-danger-600 text-xl sm:text-2xl" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
            Failed to Load Wallets
          </h2>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
          <button
            onClick={handleGoBack}
            className="btn-primary text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
          >
            Go Back
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/20 to-primary-300/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-indigo-300/20 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-white/20 px-3 sm:px-4 md:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <button
              onClick={() => setSidebarOpen && setSidebarOpen(true)}
              className="block xl:hidden text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
            >
              <MenuIcon className="text-xl sm:text-2xl" />
            </button>
            <button
              onClick={handleGoBack}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 rounded-xl p-1.5 sm:p-2 transition-all duration-200"
            >
              <ArrowBackIcon className="text-xl sm:text-2xl" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent truncate">
                Convert Funds
              </h1>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base truncate">
                Convert between different currencies
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        <div className="max-w-4xl mx-auto">
          {/* Source Wallet Card */}
          {sourceWallet && (
            <div className="fintech-card p-4 sm:p-6 mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
                Source Wallet
              </h2>
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center font-bold text-lg sm:text-xl shadow-sm ${getCurrencyCode(sourceWallet) === "NGN"
                      ? "bg-green-100 text-green-700"
                      : getCurrencyCode(sourceWallet) === "USD"
                        ? "bg-blue-100 text-blue-700"
                        : getCurrencyCode(sourceWallet) === "USDT"
                          ? "bg-green-100 text-green-700"
                          : getCurrencyCode(sourceWallet) === "GBP"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-gray-100 text-gray-700"
                    }`}
                >
                  {getCurrencyCode(sourceWallet) === "USDT" ? (
                    <SiTether className="text-lg sm:text-xl" />
                  ) : (
                    getFlag(getCurrencyCode(sourceWallet))
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-sm sm:text-base font-semibold text-gray-900">
                    {sourceWallet.ownership_label ||
                      `${getCurrencyCode(sourceWallet)} Wallet`}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {sourceWallet.wallet_number}
                  </div>
                  <div className="text-sm sm:text-base font-bold text-gray-900 mt-1">
                    {getSymbol(getCurrencyCode(sourceWallet))}
                    {parseFloat(sourceWallet.balance ?? 0).toLocaleString(
                      undefined,
                      { minimumFractionDigits: 2 }
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Convert Steps */}
          <div className="fintech-card p-4 sm:p-6">
            <Stepper activeStep={activeStep} className="mb-6 sm:mb-8">
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            <div className="min-h-[400px]">
              {activeStep === 0 && (
                <div className="space-y-4">
                  <Typography variant="h6" className="mb-4">
                    Enter Conversion Details
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Convert To Currency"
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}
                    sx={{ mb: 3 }}
                  >
                    {supportedCurrencies.map((currencyCode) => (
                      <MenuItem key={currencyCode} value={currencyCode}>
                        <div className="flex items-center gap-2">
                          {/* {currencyCode === "USDT" ? (
                            <SiTether className="text-lg" />
                          ) : (
                            getFlag(currencyCode)
                          )} */}
                          {currencyCode}
                        </div>
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {sourceWallet
                            ? getSymbol(getCurrencyCode(sourceWallet))
                            : "₦"}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              )}

              {activeStep === 1 && (
                <div className="space-y-4">
                  <Typography variant="h6" className="mb-4">
                    Get Exchange Rate
                  </Typography>
                  <Paper
                    elevation={1}
                    sx={{ p: 3, mb: 3, background: "#f8fafc" }}
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">From:</span>
                        <span className="font-semibold">
                          {sourceWallet
                            ? getSymbol(getCurrencyCode(sourceWallet))
                            : "₦"}
                          {amount} (
                          {sourceWallet ? getCurrencyCode(sourceWallet) : "N/A"}
                          )
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">To:</span>
                        <span className="font-semibold">
                          {getFlag(toCurrency)} {toCurrency}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Wallet:</span>
                        <span className="font-semibold">
                          {sourceWallet
                            ? sourceWallet.wallet_number || sourceWallet.id
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </Paper>
                  <Button
                    variant="contained"
                    onClick={handleGetExchangeRate}
                    disabled={exchangeRateLoading}
                    fullWidth
                  >
                    {exchangeRateLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Get Exchange Rate"
                    )}
                  </Button>
                </div>
              )}

              {activeStep === 2 && exchangeRateInfo && (
                <div className="space-y-4">
                  <Typography variant="h6" className="mb-4">
                    Confirm Conversion
                  </Typography>

                  <Paper
                    elevation={1}
                    sx={{ p: 3, mb: 3, background: "#f8fafc" }}
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Exchange Rate:</span>
                        <span className="font-semibold">
                          1 {exchangeRateInfo.init_currency_code} ={" "}
                          {exchangeRateInfo.rate}{" "}
                          {exchangeRateInfo.final_currency_code}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">You will receive:</span>
                        <span className="font-semibold">
                          {getSymbol(toCurrency)}
                          {exchangeRateInfo.quote_amount.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">From:</span>
                        <span className="font-semibold">
                          {sourceWallet
                            ? getSymbol(getCurrencyCode(sourceWallet))
                            : "₦"}
                          {exchangeRateInfo.init_amount} (
                          {exchangeRateInfo.init_currency_code})
                        </span>
                      </div>
                    </div>
                  </Paper>

                  <TextField
                    fullWidth
                    label="Transaction PIN"
                    type={showPin ? "text" : "password"}
                    value={pin}
                    onChange={(e) =>
                      setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    sx={{ mb: 3 }}
                    inputProps={{ maxLength: 4 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPin(!showPin)}
                            edge="end"
                          >
                            {showPin ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              )}

              {activeStep === 3 && convertResult && (
                <div className="space-y-4">
                  <Typography
                    variant="h6"
                    color="success.main"
                    className="mb-4"
                  >
                    Conversion Complete!
                  </Typography>

                  <Paper
                    elevation={1}
                    sx={{ p: 3, mb: 3, background: "#e8f5e8" }}
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">From:</span>
                        <span className="font-semibold">
                          {getSymbol(convertResult.from_currency)}
                          {convertResult.from_amount} (
                          {convertResult.from_currency})
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">To:</span>
                        <span className="font-semibold">
                          {getSymbol(convertResult.to_currency)}
                          {convertResult.to_amount} ({convertResult.to_currency}
                          )
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Exchange Rate:</span>
                        <span className="font-semibold">
                          {convertResult.exchange_rate}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Transaction ID:</span>
                        <span className="font-semibold">
                          {convertResult.transaction_id}
                        </span>
                      </div>
                    </div>
                  </Paper>
                </div>
              )}

              {convertError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {convertError}
                </Alert>
              )}
            </div>

            {/* Step Actions */}
            <div className="flex justify-between mt-6 sm:mt-8">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <div>
                {activeStep === steps.length - 1 ? (
                  <Button variant="contained" onClick={handleReset}>
                    Convert Another
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={activeStep === 2 ? handleConvert : handleNext}
                    disabled={
                      converting ||
                      (activeStep === 0 && (!amount || Number(amount) <= 0)) ||
                      (activeStep === 2 && !pin)
                    }
                  >
                    {converting ? (
                      <CircularProgress size={20} />
                    ) : activeStep === 2 ? (
                      "Convert"
                    ) : (
                      "Next"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}
